import { Tabs } from '@material-ui/core';
import { useMemo, useContext, createContext } from 'react';
import { Waypoint } from 'react-waypoint';
import { useScrollSpyWaypoint } from './use-scrollspy';

const ScrollSpyContext = createContext<SrollSpyState>(null);

export const ScrollSpyBoundry = ({
  children,
  topOffset = '160px',
  bottomOffset = '80%',
  defaultItem = null,
  ...props
}: ScrollSpyBoundryProps) => {
  const { currentTab, onTabChange, onWayPointEnter } = useScrollSpyWaypoint(defaultItem as string);

  const state = useMemo(
    () => ({
      getWaypointProps: (id: string) => ({
        topOffset,
        bottomOffset,
        ...props,
        onEnter: onWayPointEnter(id),
      }),
      tabsProps: {
        onChange: onTabChange,
        value: currentTab,
      },
    }),
    [currentTab, onTabChange, onWayPointEnter]
  );
  return <ScrollSpyContext.Provider value={state}>{children}</ScrollSpyContext.Provider>;
};

export const ScrollSpyTabs: typeof Tabs = props => {
  const { tabsProps } = useContext(ScrollSpyContext);
  return <Tabs {...props} {...tabsProps} />;
};

export const ScrollSpyItem = <J extends { id: string }>({
  children,
}: {
  children: React.ReactElement<J>;
}) => {
  const id = children.props.id;
  const { getWaypointProps } = useContext(ScrollSpyContext);
  if (!id) {
    throw 'An id attr on child is required for scrollspy to work.';
  }
  return <Waypoint {...getWaypointProps(id)}>{children}</Waypoint>;
};

/**
 * Convert a component to a scrollspied component.
 */
export function withScrollSpyItem<T extends React.ComponentType<{ id: string }>>(Component: T): T {
  const wrapped = ((props: any) => {
    // const Render: any = Component;
    const { getWaypointProps } = useContext(ScrollSpyContext);
    return (
      <Waypoint {...getWaypointProps(props.id)}>
        <Component {...props} />
      </Waypoint>
    );
  }) as T;
  wrapped.displayName = `WithSSI(${Component.displayName || Component.name})`;
  return wrapped;
}

export type ScrollSpyBoundryProps = Pick<
  Waypoint.WaypointProps,
  'topOffset' | 'bottomOffset' | 'debug' | 'horizontal' | 'fireOnRapidScroll'
> & { children: React.ReactNode; defaultItem?: string | number };

type SrollSpyState = {
  getWaypointProps: (id: string | number) => Waypoint.WaypointProps;
  tabsProps: {
    onChange: (e: any, i: any) => void;
    value: string;
  };
};
